// @flow

import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';

import ImageFileDrop from '../SharedComponents/ImageFileDrop';
import RemovableModalImage from '../SharedComponents/RemovableModalImage';
import RemoveKeywordTriggerDialog from './RemoveKeywordTriggerDialog';
import ForwardRef from '../SharedComponents/ForwardRef';

import { calculateCreditsForMsg } from '../../models/Message';
import { log } from '../../utils/jsUtils';
import { setSnackMessage } from '../../reduxStore/modules/common';

import { useTheme } from '@mui/material/styles';
import keywordDetailsDialogStyles from './Styles/KeywordDetailsDialog.Style';

type Props = {
  isNewKeyword: boolean,
  keyword: any,
  setKeyword: (val?: any) => void,
  removeKeywordTrigger: (kw: any) => void,
  setOpen: (val: boolean) => void,
  onClose: Function,
  open: boolean,
  triggerAction: Function
};

const KeywordDetailsDialog = (props: Props) => {
  const {
    isNewKeyword,
    keyword,
    setKeyword,
    removeKeywordTrigger,
    setOpen,
    onClose,
    open,
    triggerAction
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();

  // Local state
  const [attachment, setAttachment] = useState(null);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [openDeleteKeywordDialog, setOpenDeleteKeywordDialog] = useState(false);

  // React Hook Form
  const { register, errors } = useForm();

  // On mount or when `keyword` changes, recalc credits
  useEffect(() => {
    if (open && keyword) {
      const [att] = keyword.ResponseAttachments?.length > 0
        ? keyword.ResponseAttachments
        : [null];
      const cu = calculateCreditsForMsg({
        message: keyword.Response,
        attachment: att
      });
      setCreditsUsed(cu);
    }
  }, [keyword, open]);

  // Clears out local form
  const emptyForm = () => {
    setKeyword();
    setAttachment(null);
  };

  // Handler to confirm removing the keyword
  const handleRemoveKeywordTrigger = () => {
    if (keyword) {
      removeKeywordTrigger(keyword);
      setKeyword(null);
      setOpen(false);
    } else {
      dispatch(setSnackMessage({
        message: 'An error occured. Please try again.',
        variant: 'error',
        duration: 3000
      }));
    }
  };

  // Add or remove attachment in local state
  const handleSetAttachment = (attachmentObj: { Url: string, MimeType: string }) => {
    log('[handleSetAttachment] attachmentObj: ', attachmentObj);
    setAttachment(attachmentObj);
  };

  const removeAttachment = (att) => (e) => {
    e.stopPropagation();
    setAttachment(null);
    setKeyword({
      ...keyword,
      ResponseAttachments: []
    });
  };

  return (
    <Dialog
      aria-labelledby="form-add-keyword"
      open={open}
      onClose={onClose}
      // Apply style to the Dialog's Paper:
      PaperProps={{
        sx: keywordDetailsDialogStyles.dialogPaperCtr(theme)
      }}
    >
      <form
        autoComplete="off"
        noValidate
        // "xlarge" class for size, plus inline style for MUI 5
        className={classNames('xlarge')}
        style={keywordDetailsDialogStyles.dialogFormCtr(theme)}
        onSubmit={(e) => {
          e.preventDefault();
          log('[KeywordDetails] saving keyword', keyword);
          triggerAction(
            {
              ...keyword,
              ResponseAttachments: attachment ? [attachment] : []
            },
            attachment
          );
          emptyForm();
          setOpen(false);
        }}
      >
        <DialogContent
          sx={keywordDetailsDialogStyles.dialogContentCtr(theme)}
        >
          {/* Title: "If a customer sends this keyword" */}
          <div
            style={keywordDetailsDialogStyles.processLegend(theme)}
          >
            <Typography variant="h3">
              {'If a customer'}
            </Typography>
          </div>

          <div
            style={keywordDetailsDialogStyles.processLegend(theme)}
          >
            <Typography variant="h3">
              {'sends this keyword:'}
            </Typography>
          </div>

          {/* Keyword text */}
          <TextField
            autoFocus
            sx={keywordDetailsDialogStyles.textField(theme)}
            fullWidth
            id="keywordName"
            label="Type keyword"
            margin="dense"
            multiline
            variant="outlined"
            name="keyword"
            inputRef={register({ required: true })}
            value={keyword?.Keyword || ''}
            onChange={(e) =>
              setKeyword({
                ...keyword,
                Keyword: e.target.value
              })
            }
            required
          />
          <div style={keywordDetailsDialogStyles.errorLabel(theme)}>
            {errors.keyword && 'Keyword is required.'}
          </div>

          <div
            style={keywordDetailsDialogStyles.processLegend(theme)}
          >
            <ArrowDownwardRoundedIcon
              sx={keywordDetailsDialogStyles.arrowIcon(theme)}
            />
          </div>

          <div
            style={keywordDetailsDialogStyles.processLegend(theme)}
          >
            <Typography variant="h3">
              {'then Neo replies:'}
            </Typography>
          </div>

          {/* Reply text */}
          <TextField
            sx={keywordDetailsDialogStyles.textField(theme)}
            fullWidth
            id="keywordResponse"
            label="Type a reply"
            margin="dense"
            multiline
            rows={5}
            variant="outlined"
            name="response"
            inputRef={register({ required: true })}
            value={keyword?.Response || ''}
            onChange={(e) =>
              setKeyword({
                ...keyword,
                Response: e.target.value
              })
            }
            required
          />
          <div style={keywordDetailsDialogStyles.errorLabel(theme)}>
            {errors.response && 'Response is required.'}
          </div>

          {/* Credits used / char count */}
          {process.env.REACT_APP_CHARS_NB_PER_CREDIT && keyword?.Response && (
            <Tooltip
              aria-label={`${creditsUsed} ${creditsUsed > 1 ? 'Credits' : 'Credit'}`}
              placement="top-end"
              title={`${creditsUsed} ${creditsUsed > 1 ? 'Credits' : 'Credit'}`}
            >
              <ForwardRef>
                <div
                  style={{
                    ...keywordDetailsDialogStyles.textFieldCaptionRight(theme),
                    ...(keyword?.Response.length >= 1600
                      ? keywordDetailsDialogStyles.warning(theme)
                      : {})
                  }}
                >
                  {keyword?.Response.length >= 1600
                    ? '1600 characters max per message.'
                    : keyword?.Response.length > 0 && keyword?.Response.length < 1600
                      ? `${process.env.REACT_APP_CHARS_NB_PER_CREDIT
                        - (keyword.Response.length % process.env.REACT_APP_CHARS_NB_PER_CREDIT)} / ${creditsUsed}`
                      : <span>&nbsp;</span>}
                </div>
              </ForwardRef>
            </Tooltip>
          )}

          {/* If we have an attachment, display it */}
          {attachment?.Url ? (
            <RemovableModalImage
              src={attachment.Url}
              onRemove={removeAttachment(attachment)}
            />
          ) : keyword?.ResponseAttachments?.length > 0 ? (
            keyword.ResponseAttachments.map((att, attIdx) => (
              <RemovableModalImage
                key={`attachment-${attIdx}`}
                src={att.Url}
                onRemove={removeAttachment(att)}
              />
            ))
          ) : null}
        </DialogContent>

        <DialogActions
          sx={keywordDetailsDialogStyles.actionsCtr(theme)}
        >
          {/* Attachment button */}
          <div>
            <ImageFileDrop onImageReceived={handleSetAttachment}>
              <Tooltip
                aria-label="Attach an image"
                placement="top"
                title="Attach an image"
              >
                <Fab
                  aria-label="Attach an image"
                  sx={keywordDetailsDialogStyles.actionAttachImgBtn(theme)}
                  color="secondary"
                >
                  <ImageOutlinedIcon />
                </Fab>
              </Tooltip>
            </ImageFileDrop>
          </div>

          {/* CTA buttons */}
          <div style={keywordDetailsDialogStyles.ctaCtr(theme)}>
            {!isNewKeyword && (
              <IconButton
                aria-label="delete"
                sx={keywordDetailsDialogStyles.iconButton(theme)}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDeleteKeywordDialog(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}

            <Button
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                emptyForm();
                setOpen(false);
              }}
              type="button"
            >
              {'Cancel'}
            </Button>

            <Button
              sx={keywordDetailsDialogStyles.smallMarginLeft(theme)}
              color="primary"
              variant="contained"
              type="submit"
            >
              {'Save'}
            </Button>
          </div>
        </DialogActions>
      </form>

      <RemoveKeywordTriggerDialog
        keyword={keyword}
        open={openDeleteKeywordDialog}
        setOpen={setOpenDeleteKeywordDialog}
        actionConfirm={handleRemoveKeywordTrigger}
      />
    </Dialog>
  );
};

export default React.memo(KeywordDetailsDialog);